import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Table, Alert, Badge, Card } from "react-bootstrap";
import { FaUserPlus, FaPlus, FaTrash, FaCheck, FaTimes, FaSearch, FaCamera, FaUsers, FaExclamationTriangle } from "react-icons/fa";

const AddStudentsModal = ({ show, onHide, schoolid, getClassesBySchoolId, addStudents }) => {
  const [rawStudentText, setRawStudentText] = useState("");
  const [parsedStudents, setParsedStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Parse options
  const [parseMode, setParseMode] = useState("newline"); // 'newline', 'comma', 'tab'

  // Filter states
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [classAcademicYearFilter, setClassAcademicYearFilter] = useState("all");
  const [classSnapshotFilter, setClassSnapshotFilter] = useState("all");
  const [classSearchTerm, setClassSearchTerm] = useState("");

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Classes" },
    { value: "active", label: "Active Classes Only" },
    { value: "snapshot", label: "Snapshots Only" }
  ];

  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  useEffect(() => {
    if (show && schoolid) {
      loadClasses();
    }
  }, [show, schoolid]);

  useEffect(() => {
    parseStudentNames();
  }, [rawStudentText, parseMode]);

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  const loadClasses = async () => {
    setLoading(true);
    setError("");
    try {
      const classesResponse = await getClassesBySchoolId(schoolid);
      
      const transformedClasses = (classesResponse || []).map(classItem => {
        const gradeLabel = getGradeLabelFromClass(classItem);
        const gradeBoolean = getGradeBooleanFromClass(classItem);
        
        return {
          id: classItem.id,
          name: classItem.name,
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          academicYear: classItem.academicYear || null, // Use direct field from API
          snapshot: classItem.snapshot || false, // Add snapshot field
          studentNumber: classItem.studentNumber || 0, // Add student count
          teacher: classItem.teacher?.name || classItem.teacherName || '',
          active: classItem.active,
          trial_class: classItem.trial_class,
          date_updated: classItem.date_updated
        };
      }).sort((a, b) => {
        // Sort by academic year (newest first), then by name
        if (a.academicYear !== b.academicYear) {
          return b.academicYear?.localeCompare(a.academicYear || '') || 0;
        }
        return a.name.localeCompare(b.name);
      });

      // Extract unique academic years for filter options
      const uniqueAcademicYears = [...new Set(transformedClasses
        .map(cls => cls.academicYear)
        .filter(year => year) // Remove null/undefined
      )].sort().reverse(); // Sort newest first

      const academicYearOptions = [
        { value: "all", label: "All Academic Years" },
        ...uniqueAcademicYears.map(year => ({
          value: year,
          label: `${year} Academic Year`
        }))
      ];

      setAvailableAcademicYears(academicYearOptions);
      setClasses(transformedClasses);
    } catch (err) {
      setError("Failed to load classes. Please try again.");
      console.error("Error loading classes:", err);
    } finally {
      setLoading(false);
    }
  };

  const parseStudentNames = () => {
    if (!rawStudentText.trim()) {
      setParsedStudents([]);
      return;
    }

    let separator;
    switch (parseMode) {
      case 'comma':
        separator = ',';
        break;
      case 'tab':
        separator = '\t';
        break;
      case 'newline':
      default:
        separator = '\n';
        break;
    }

    const names = rawStudentText
      .split(separator)
      .map(name => name.trim())
      .filter(name => name.length > 0)
      .map((name, index) => {
        // Try to parse first name and last name
        const nameParts = name.split(' ').filter(part => part.trim());
        let firstName = '';
        let lastName = '';

        if (nameParts.length === 1) {
          firstName = nameParts[0];
        } else if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        }

        return {
          id: `temp-${index}`, // Temporary ID for tracking
          originalName: name,
          firstName: firstName,
          lastName: lastName,
          displayName: name,
          // Student properties based on your actual Student model
          gender: '',
          age: null,
          sen: false,        // Special Educational Needs
          esl: false,        // English as Second Language
          asd: false,        // Autism Spectrum Disorder
          gdd: false,        // Global Developmental Delay
          send: false,       // Special Educational Needs and Disabilities
          country: '',
          currentGrade: '',
          isSpecialEd: false,
          // Validation
          isValid: firstName.length > 0,
          validationError: firstName.length === 0 ? "Name cannot be empty" : ""
        };
      });

    setParsedStudents(names);
  };

  const updateStudentField = (studentId, field, value) => {
    setParsedStudents(prev => 
      prev.map(student => 
        student.id === studentId 
          ? { ...student, [field]: value }
          : student
      )
    );
  };

  const getFilteredClasses = () => {
    return classes.filter(classItem => {
      const matchesGrade = classGradeFilter === "all" || classItem.gradeBoolean === classGradeFilter;
      const matchesAcademicYear = classAcademicYearFilter === "all" || classItem.academicYear === classAcademicYearFilter;
      
      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (classSnapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (classSnapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }
      
      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
                           (classItem.teacher && classItem.teacher.toLowerCase().includes(classSearchTerm.toLowerCase()));
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });
  };

  const removeStudent = (studentId) => {
    setParsedStudents(prev => prev.filter(student => student.id !== studentId));
  };

  const addManualStudent = () => {
    const newStudent = {
      id: `manual-${Date.now()}`,
      originalName: "",
      firstName: "",
      lastName: "",
      displayName: "",
      gender: '',
      age: null,
      sen: false,
      esl: false,
      asd: false,
      gdd: false,
      send: false,
      country: '',
      currentGrade: '',
      isSpecialEd: false,
      isValid: false,
      validationError: "Please enter a name"
    };
    setParsedStudents(prev => [...prev, newStudent]);
  };

  const updateManualStudentName = (studentId, firstName, lastName) => {
    const displayName = `${firstName} ${lastName}`.trim();
    const isValid = firstName.trim().length > 0;
    
    setParsedStudents(prev => 
      prev.map(student => 
        student.id === studentId 
          ? { 
              ...student, 
              firstName: firstName,
              lastName: lastName,
              displayName: displayName,
              originalName: displayName,
              isValid: isValid,
              validationError: isValid ? "" : "First name is required"
            }
          : student
      )
    );
  };

  const handleAddStudents = async () => {
    if (!selectedClass) {
      setError("Please select a class.");
      return;
    }

    const validStudents = parsedStudents.filter(student => student.isValid);
    if (validStudents.length === 0) {
      setError("Please add at least one valid student.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare student data for API - matching your Student model schema
      const studentsToAdd = validStudents.map(student => ({
        name: `${student.firstName} ${student.lastName}`.trim(),
        gender: student.gender || undefined,
        age: student.age || undefined,
        sen: student.sen,
        esl: student.esl,
        asd: student.asd,
        gdd: student.gdd,
        send: student.send,
        country: student.country || undefined,
        currentGrade: student.currentGrade || undefined,
        isSpecialEd: student.isSpecialEd
      }));

      // Call your existing API function with classid and student array
      const response = await addStudents(selectedClass.id, studentsToAdd);

      if (response) {
        setSuccess(`Successfully added ${validStudents.length} student(s) to ${selectedClass.name}`);
        
        // Reset form
        setRawStudentText("");
        setParsedStudents([]);
        setSelectedClass(null);
      } else {
        setError("Failed to add students. Please try again.");
      }

    } catch (err) {
      setError("Failed to add students. Please try again.");
      console.error("Add students error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRawStudentText("");
    setParsedStudents([]);
    setSelectedClass(null);
    setError("");
    setSuccess("");
    setParseMode("newline");
    setClassGradeFilter("all");
    setClassAcademicYearFilter("all");
    setClassSnapshotFilter("all");
    setClassSearchTerm("");
    onHide();
  };

  const filteredClasses = getFilteredClasses();
  const validStudentsCount = parsedStudents.filter(s => s.isValid).length;

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      backdrop="static"
      dialogClassName="custom-modal-width"
    >
      <style>
        {`
          .custom-modal-width {
            max-width: 90% !important;
          }
          .custom-modal-width .modal-dialog {
            max-width: 90% !important;
          }
        `}
      </style>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUserPlus className="me-2" />
          Add Students
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ minHeight: '600px' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="h-100">
          {/* Column 1: Input Text Area */}
          <Col md={4} className="border-end">
            <div className="mb-3">
              <h5 className="mb-3">Paste Student Names</h5>
              
              {/* Parse Mode Selection */}
              <Form.Group className="mb-3">
                <Form.Label>Separator Type</Form.Label>
                <Form.Select 
                  value={parseMode} 
                  onChange={(e) => setParseMode(e.target.value)}
                  size="sm"
                >
                  <option value="newline">New Line</option>
                  <option value="comma">Comma</option>
                  <option value="tab">Tab</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  How names are separated in your text
                </Form.Text>
              </Form.Group>

              {/* Text Area */}
              <Form.Group className="mb-3">
                <Form.Label>Student Names</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={12}
                  placeholder={`Paste student names here...\n\nExample:\nJohn Smith\nMary Johnson\nPeter O'Connor`}
                  value={rawStudentText}
                  onChange={(e) => setRawStudentText(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Paste names separated by {parseMode === 'newline' ? 'new lines' : parseMode === 'comma' ? 'commas' : 'tabs'}
                </Form.Text>
              </Form.Group>

              <Button 
                variant="outline-primary" 
                onClick={addManualStudent}
                className="w-100 mb-2"
              >
                <FaPlus className="me-1" />
                Add Single Student
              </Button>

              {parsedStudents.length > 0 && (
                <div className="text-center">
                  <Badge bg="info">
                    {parsedStudents.length} parsed ({validStudentsCount} valid)
                  </Badge>
                </div>
              )}
            </div>
          </Col>

          {/* Column 2: Student Table */}
          <Col md={5} className="border-end">
            <div className="mb-3">
              <h5 className="mb-3">
                Students to Add
                <Badge bg="primary" className="ms-2">{validStudentsCount}</Badge>
              </h5>

              <div style={{ height: '500px', overflowY: 'auto' }}>
                {parsedStudents.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    Paste student names in the left panel to see them here
                  </div>
                ) : (
                  <Table striped bordered hover size="sm">
                    <thead className="sticky-top bg-light">
                      <tr>
                        <th>Name</th>
                        <th>SEN</th>
                        <th>ESL</th>
                        <th>ASD</th>
                        <th>GDD</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedStudents.map(student => (
                        <tr key={student.id} className={!student.isValid ? 'table-warning' : ''}>
                          <td>
                            {student.id.startsWith('manual-') ? (
                              <Row>
                                <Col xs={6}>
                                  <Form.Control
                                    size="sm"
                                    placeholder="First name"
                                    value={student.firstName}
                                    onChange={(e) => updateManualStudentName(student.id, e.target.value, student.lastName)}
                                  />
                                </Col>
                                <Col xs={6}>
                                  <Form.Control
                                    size="sm"
                                    placeholder="Last name"
                                    value={student.lastName}
                                    onChange={(e) => updateManualStudentName(student.id, student.firstName, e.target.value)}
                                  />
                                </Col>
                              </Row>
                            ) : (
                              <div>
                                <div className="fw-medium">{student.displayName}</div>
                                {!student.isValid && (
                                  <small className="text-danger">{student.validationError}</small>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={student.sen}
                              onChange={(e) => updateStudentField(student.id, 'sen', e.target.checked)}
                              title="Special Educational Needs"
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={student.esl}
                              onChange={(e) => updateStudentField(student.id, 'esl', e.target.checked)}
                              title="English as Second Language"
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={student.asd}
                              onChange={(e) => updateStudentField(student.id, 'asd', e.target.checked)}
                              title="Autism Spectrum Disorder"
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={student.gdd}
                              onChange={(e) => updateStudentField(student.id, 'gdd', e.target.checked)}
                              title="Global Developmental Delay"
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeStudent(student.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </Col>

          {/* Column 3: Class Selection */}
          <Col md={3}>
            <div className="mb-3">
              <h5 className="mb-3">Select Class</h5>

              {/* Class Filters */}
              {/* Search Box - Full Width Row */}
              <Row className="mb-3">
                <Col>
                  <Form.Label className="small">Search</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Search classes..."
                      value={classSearchTerm}
                      onChange={(e) => setClassSearchTerm(e.target.value)}
                      size="sm"
                    />
                    <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                  </div>
                </Col>
              </Row>

              {/* Filter Dropdowns */}
              <Row className="mb-2">
                <Col md={12}>
                  <Form.Label className="small">Academic Year</Form.Label>
                  <Form.Select 
                    value={classAcademicYearFilter} 
                    onChange={(e) => setClassAcademicYearFilter(e.target.value)}
                    size="sm"
                  >
                    {availableAcademicYears.map(year => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={12}>
                  <Form.Label className="small">Grade Level</Form.Label>
                  <Form.Select 
                    value={classGradeFilter} 
                    onChange={(e) => setClassGradeFilter(e.target.value)}
                    size="sm"
                  >
                    {gradeLevels.map(grade => (
                      <option key={grade.value} value={grade.value}>
                        {grade.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label className="small">Class Type</Form.Label>
                  <Form.Select 
                    value={classSnapshotFilter} 
                    onChange={(e) => setClassSnapshotFilter(e.target.value)}
                    size="sm"
                  >
                    {snapshotOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {selectedClass && (
                <>
                  <Card className="mb-3 border-success">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <div className="fw-medium text-success">
                              <FaCheck className="me-1" />
                              {selectedClass.name}
                            </div>
                            {selectedClass.snapshot && (
                              <Badge bg="secondary" className="ms-2">
                                <FaCamera className="me-1" size={10} />
                                Snapshot
                              </Badge>
                            )}
                          </div>
                          <small className="text-muted">
                            {selectedClass.grade}
                            {selectedClass.teacher && ` • ${selectedClass.teacher}`}
                            {selectedClass.academicYear && ` • ${selectedClass.academicYear}`}
                            {!selectedClass.snapshot && selectedClass.studentNumber > 0 && (
                              <span> • {selectedClass.studentNumber} students</span>
                            )}
                          </small>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setSelectedClass(null)}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>

                  {selectedClass.snapshot && (
                    <Alert variant="warning" className="mb-3 py-2">
                      <FaExclamationTriangle className="me-2" />
                      <strong>Snapshot Class:</strong> Adding students to historical records.
                    </Alert>
                  )}
                </>
              )}

              <div style={{ height: '300px', overflowY: 'auto' }}>
                {loading ? (
                  <div className="text-center py-4">Loading classes...</div>
                ) : filteredClasses.length === 0 ? (
                  <div className="text-center py-4 text-muted">No classes found matching filters</div>
                ) : (
                  <div className="d-grid gap-2">
                    {filteredClasses.map(classItem => (
                      <Card
                        key={classItem.id}
                        className={`cursor-pointer ${
                          selectedClass?.id === classItem.id ? 'border-success bg-light' : ''
                        }`}
                        onClick={() => setSelectedClass(classItem)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Card.Body className="py-2">
                          <div className="d-flex align-items-center mb-1">
                            <div className="fw-medium me-2">{classItem.name}</div>
                            {classItem.snapshot && (
                              <Badge bg="secondary" className="me-2">
                                <FaCamera className="me-1" size={10} />
                                Snapshot
                              </Badge>
                            )}
                            {classItem.trial_class && (
                              <Badge bg="warning" text="dark">Trial</Badge>
                            )}
                          </div>
                          <small className="text-muted">
                            {classItem.grade}
                            {classItem.teacher && ` • ${classItem.teacher}`}
                            {classItem.academicYear && ` • ${classItem.academicYear}`}
                            {!classItem.snapshot && classItem.studentNumber > 0 && (
                              <Badge bg="light" text="dark" className="ms-2">
                                <FaUsers className="me-1" size={10} />
                                {classItem.studentNumber} student{classItem.studentNumber !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </small>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {validStudentsCount > 0 && selectedClass && (
              <span>
                Adding {validStudentsCount} student(s) to {selectedClass.name}
                {selectedClass.snapshot && (
                  <Badge bg="warning" className="ms-2 text-dark">
                    <FaCamera className="me-1" size={10} />
                    Snapshot Class
                  </Badge>
                )}
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleAddStudents}
              disabled={validStudentsCount === 0 || !selectedClass || loading}
            >
              {loading ? "Adding Students..." : `Add ${validStudentsCount} Student(s)`}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddStudentsModal;